import { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { AccountInfo, InteractionStatus, IPublicClientApplication } from '@azure/msal-browser';
import { loginRequest } from '../auth/msalConfig';
import { invalidatePermissionCache } from './usePagePermissions';
import { logger } from '../utils/logger';

interface UseMicrosoftAuthReturn {
  isAuthenticated: boolean;
  isLoading: boolean;
  account: AccountInfo | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  error: string | null;
  instance: IPublicClientApplication;
}

export const useMicrosoftAuth = (): UseMicrosoftAuthReturn => {
  const { instance, accounts, inProgress } = useMsal();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const account = accounts[0] || null;
  const isAuthenticated = !!account;

  useEffect(() => {
    // Expose MSAL instance on window for useFirebaseInit hook
    // This allows Firebase authentication to acquire MSAL tokens
    window.msalInstance = instance;
  }, [instance]);

  useEffect(() => {
    // Handle redirect promise on page load
    instance
      .handleRedirectPromise()
      .then(() => {
        setIsLoading(false);
      })
      .catch((err) => {
        logger.error('Redirect promise error:', err);
        setError(err.message);
        setIsLoading(false);
      });
  }, [instance]);

  useEffect(() => {
    // Update loading state based on interaction progress
    if (inProgress === InteractionStatus.None) {
      setIsLoading(false);
    }
  }, [inProgress]);

  const login = async () => {
    setError(null);
    setIsLoading(true);

    // Use redirect authentication due to network restrictions blocking Microsoft CDN
    // Popup authentication fails because the popup window can't load Microsoft's assets
    // from aadcdn.msauth.net (blocked by local network). Redirect authentication works
    // because it goes through Cloudflare tunnel which bypasses the network restrictions.
    try {
      await instance.loginRedirect(loginRequest);
    } catch (redirectError) {
      logger.error('Redirect login failed:', redirectError);
      setError(redirectError instanceof Error ? redirectError.message : String(redirectError));
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setError(null);

    // Clear permission cache on logout to ensure fresh permissions on next login
    invalidatePermissionCache();

    // Use redirect logout to match redirect login (consistent UX)
    try {
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin + '/rentals',
      });
    } catch (redirectError) {
      logger.error('Logout failed:', redirectError);
      setError(redirectError instanceof Error ? redirectError.message : String(redirectError));
    }
  };

  return {
    isAuthenticated,
    isLoading,
    account,
    login,
    logout,
    error,
    instance,
  };
};
